/**
 * useBgImages.hook.ts
 *
 * Loads background image metadata from the bundled asset manifest and
 * manages selection state for the BgImagesPanel.
 *
 * Images live at: assets/bg-images/*.{jpg,png,webp}
 * Webpack bundles them and exposes their URLs via the BG_IMAGE_MANIFEST
 * (see webpack.config.js additions below).
 */

import { useState, useEffect, useCallback } from "react";
import { imageUrlToBase64 } from "../../word/document/applyHeroBgImage";
import { logger } from "../../core/logging/index";

export interface BgImageEntry {
  id: string;
  filename: string;
  /** Webpack-resolved URL (e.g. /assets/bg-images/mountains.jpg) */
  url: string;
  /** Optional thumbnail URL for UI previews */
  thumbnailUrl?: string;
  /** Display label derived from filename — "mountains.jpg" → "Mountains" */
  label: string;
}

export interface UseBgImagesReturn {
  images: BgImageEntry[];
  selectedId: string | null;
  isLoading: boolean;
  select: (id: string) => void;
  getSelectedBase64: () => Promise<string | null>;
}

/**
 * Webpack requires a static require.context call to bundle asset folders.
 * Add this to your webpack.config.js plugins array or use the dynamic
 * import approach below, which works with webpack's asset modules.
 *
 * In webpack.config.js, add to module.rules:
 * {
 *   test: /\.(png|jpg|jpeg|webp)$/i,
 *   type: 'asset/resource',
 *   generator: { filename: 'assets/[name][ext]' }
 * }
 *
 * Then create src/taskpane/bgImageManifest.ts listing the images:
 * export const BG_IMAGES = [
 *   { id: 'mountains', filename: 'mountains.jpg', url: require('../../assets/bg-images/mountains.jpg') },
 *   { id: 'ocean',     filename: 'ocean.jpg',     url: require('../../assets/bg-images/ocean.jpg') },
 * ];
 *
 * This hook reads that manifest.
 */

// Dynamic import of the manifest — created separately (see bgImageManifest.ts)
// Falls back to empty array if the manifest doesn't exist yet.
let manifestModule: { BG_IMAGES: BgImageEntry[] } | null = null;
try {
  // @ts-ignore — manifest may not exist during initial setup
  manifestModule = require("../bgImageManifest");
} catch {
  logger.warn("bgImageManifest not found — no background images available", {});
}

export function useBgImages(): UseBgImagesReturn {
  const [images] = useState<BgImageEntry[]>(() => {
    const raw = manifestModule?.BG_IMAGES ?? [];
    return raw.map((img) => ({
      ...img,
      label: filenameToLabel(img.filename),
    }));
  });

  const [selectedId, setSelectedId] = useState<string | null>(
    images.length > 0 ? images[0].id : null
  );
  const [isLoading] = useState(false);

  const select = useCallback((id: string) => {
    setSelectedId(id);
  }, []);

  /**
   * Fetches the currently selected image and converts it to base64.
   * Returns null if nothing is selected.
   */
  const getSelectedBase64 = useCallback(async (): Promise<string | null> => {
    if (!selectedId) return null;
    const img = images.find((i) => i.id === selectedId);
    if (!img) return null;

    try {
      return await imageUrlToBase64(img.url);
    } catch (err) {
      logger.error("Failed to convert bg image to base64", { id: selectedId, err });
      return null;
    }
  }, [selectedId, images]);

  return { images, selectedId, isLoading, select, getSelectedBase64 };
}

export default useBgImages;

function filenameToLabel(filename: string): string {
  return filename
    .replace(/\.[^.]+$/, "")           // remove extension
    .replace(/[-_]/g, " ")             // hyphens/underscores → spaces
    .replace(/\b\w/g, (c) => c.toUpperCase()); // Title Case
}